import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, Trash2, Send, CheckCircle2, XCircle, ArrowLeft, Plus, FileText } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, DataTable } from "../components/bits";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  borrador: { label: "Borrador", cls: "bg-slate-100 text-slate-600 border-slate-300" },
  en_revision: { label: "En revisión", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  aprobada: { label: "Aprobada", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rechazada: { label: "Rechazada", cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

const PROVIDERS = [
  { key: "openai", label: "ChatGPT" },
  { key: "anthropic", label: "Claude" },
  { key: "gemini", label: "Gemini" },
];

const EMPTY_UNIT = { name: "", objectives: "", topics: "", activities: "", strategies: "", evaluations: "" };
const EMPTY_LESSON = { unit: 1, title: "", date: "", objectives: "", activities: "", evaluation: "" };

function StatusPill({ value }) {
  const s = STATUS[value] || STATUS.borrador;
  return (
    <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full border ${s.cls}`} data-testid={`planning-status-${value}`}>
      {s.label}
    </span>
  );
}

export default function Plannings() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [courses, setCourses] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ subject_id: "", course: "", section: "A", school_year: "2025-2026", provider: "openai" });
  const fileRef = useRef(null);

  const canApprove = ["superadmin", "admin"].includes(user?.role);

  const load = useCallback(() => {
    api.get("/plannings").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/r/subjects").then((r) => setSubjects(r.data)).catch(() => {});
    api.get("/r/courses").then((r) => setCourses(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const subjectName = (sid) => subjects.find((s) => s.id === sid)?.name || "—";

  const doImport = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Selecciona un archivo primero"); return; }
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      const res = await api.post("/plannings/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      toast.success("La IA interpretó tu planificación — revísala y ajústala antes de enviarla");
      setImportOpen(false);
      if (fileRef.current) fileRef.current.value = "";
      load();
      openDetail(res.data.id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAnalyzing(false);
    }
  };

  const openDetail = (id) => {
    api.get(`/plannings/${id}`).then((r) => setDetail(r.data)).catch((e) => toast.error(formatApiError(e)));
  };

  const saveDetail = async () => {
    try {
      const res = await api.put(`/plannings/${detail.id}`, {
        title: detail.title, subject_id: detail.subject_id, course: detail.course,
        section: detail.section, school_year: detail.school_year, extracted: detail.extracted,
      });
      setDetail(res.data);
      toast.success("Planificación guardada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitReview = async () => {
    try {
      await api.post(`/plannings/${detail.id}/submit`);
      toast.success("Enviada a revisión del coordinador");
      openDetail(detail.id);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const review = async (action) => {
    const notes = action === "rechazar" ? (window.prompt("Motivo del rechazo (se lo verá el docente):") || "") : "";
    if (action === "rechazar" && !notes) return;
    try {
      await api.post(`/plannings/${detail.id}/review`, { action, notes });
      toast.success(action === "aprobar" ? "Planificación aprobada" : "Planificación rechazada");
      openDetail(detail.id);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async () => {
    if (!window.confirm("¿Eliminar esta planificación?")) return;
    try {
      await api.delete(`/plannings/${detail.id}`);
      toast.success("Planificación eliminada");
      setDetail(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setAnnual = (k, v) => setDetail((d) => ({ ...d, extracted: { ...d.extracted, annual: { ...d.extracted.annual, [k]: v } } }));
  const setUnit = (i, k, v) => setDetail((d) => {
    const units = [...d.extracted.units];
    units[i] = { ...units[i], [k]: v };
    return { ...d, extracted: { ...d.extracted, units } };
  });
  const setLesson = (i, k, v) => setDetail((d) => {
    const lessons = [...d.extracted.lessons];
    lessons[i] = { ...lessons[i], [k]: v };
    return { ...d, extracted: { ...d.extracted, lessons } };
  });

  if (detail) {
    const isOwner = detail.teacher_id === user?.id;
    const editable = (isOwner && ["borrador", "rechazada"].includes(detail.status)) || canApprove;
    const isImage = (detail.source_content_type || "").startsWith("image/");
    return (
      <div data-testid="planning-detail">
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <Button variant="outline" size="sm" data-testid="planning-back" onClick={() => setDetail(null)}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver
          </Button>
          <StatusPill value={detail.status} />
          <span className="text-xs text-slate-400">por {detail.teacher_name} · interpretada con {PROVIDERS.find((p) => p.key === detail.ai_provider)?.label || "IA"}</span>
          <div className="ml-auto flex flex-wrap gap-2">
            {editable && (
              <Button size="sm" variant="outline" data-testid="planning-save" onClick={saveDetail}>Guardar cambios</Button>
            )}
            {(isOwner || canApprove) && ["borrador", "rechazada"].includes(detail.status) && (
              <Button size="sm" className="bg-blue-800 hover:bg-blue-700" data-testid="planning-submit-review" onClick={submitReview}>
                <Send className="w-4 h-4 mr-1" /> Enviar a revisión
              </Button>
            )}
            {canApprove && detail.status === "en_revision" && (
              <>
                <Button size="sm" className="bg-emerald-700 hover:bg-emerald-600" data-testid="planning-approve" onClick={() => review("aprobar")}>
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Aprobar
                </Button>
                <Button size="sm" variant="outline" className="text-rose-600 border-rose-200" data-testid="planning-reject" onClick={() => review("rechazar")}>
                  <XCircle className="w-4 h-4 mr-1" /> Rechazar
                </Button>
              </>
            )}
            {(canApprove || (isOwner && detail.status === "borrador")) && (
              <Button size="sm" variant="ghost" className="text-rose-600" data-testid="planning-delete" onClick={remove}>
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
        {detail.status === "rechazada" && detail.review_notes && (
          <div className="mb-4 bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-700" data-testid="planning-review-notes">
            <span className="font-semibold">Observaciones del coordinador:</span> {detail.review_notes}
          </div>
        )}
        <div className="grid lg:grid-cols-2 gap-6 items-start">
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm lg:sticky lg:top-20">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" /> Documento original
            </p>
            {isImage ? (
              <img src={`${API}/files/${detail.source_file_id}`} alt="Planificación original"
                className="w-full rounded-lg border border-slate-200" data-testid="planning-original-image" />
            ) : (
              <iframe src={`${API}/files/${detail.source_file_id}`} title="Documento original"
                className="w-full h-[480px] rounded-lg border border-slate-200 bg-slate-50" data-testid="planning-original-frame" />
            )}
          </div>
          <div className="space-y-4">
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-3">
              <div className="space-y-1.5">
                <Label>Título</Label>
                <Input data-testid="planning-title" value={detail.title} disabled={!editable}
                  onChange={(e) => setDetail({ ...detail, title: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Materia</Label>
                  <Select value={detail.subject_id || "_none"} disabled={!editable}
                    onValueChange={(v) => setDetail({ ...detail, subject_id: v === "_none" ? "" : v })}>
                    <SelectTrigger data-testid="planning-subject"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">—</SelectItem>
                      {subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Curso</Label>
                  <Input data-testid="planning-course" value={detail.course} disabled={!editable}
                    onChange={(e) => setDetail({ ...detail, course: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Sección</Label>
                  <Input data-testid="planning-section" value={detail.section} disabled={!editable}
                    onChange={(e) => setDetail({ ...detail, section: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Año escolar</Label>
                  <Input data-testid="planning-year" value={detail.school_year} disabled={!editable}
                    onChange={(e) => setDetail({ ...detail, school_year: e.target.value })} />
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-3">
              <h3 className="text-sm font-semibold text-slate-800 border-b border-slate-200 pb-2">Planificación anual</h3>
              {[["objectives", "Objetivos anuales"], ["contents", "Contenidos"], ["strategies", "Estrategias pedagógicas"], ["evaluations", "Evaluaciones"]].map(([k, label]) => (
                <div key={k} className="space-y-1.5">
                  <Label>{label}</Label>
                  <Textarea rows={2} data-testid={`planning-annual-${k}`} value={detail.extracted.annual[k] || ""} disabled={!editable}
                    onChange={(e) => setAnnual(k, e.target.value)} />
                </div>
              ))}
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
              <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                <h3 className="text-sm font-semibold text-slate-800">Unidades didácticas ({detail.extracted.units.length})</h3>
                {editable && (
                  <Button size="sm" variant="outline" data-testid="planning-add-unit"
                    onClick={() => setDetail((d) => ({ ...d, extracted: { ...d.extracted, units: [...d.extracted.units, { ...EMPTY_UNIT }] } }))}>
                    <Plus className="w-4 h-4 mr-1" /> Unidad
                  </Button>
                )}
              </div>
              {detail.extracted.units.map((u, i) => (
                <div key={i} className="border border-slate-200 rounded-lg p-4 space-y-2 relative" data-testid={`planning-unit-${i}`}>
                  {editable && (
                    <button className="absolute top-2 right-2 text-slate-300 hover:text-rose-600 transition-colors" data-testid={`planning-unit-remove-${i}`}
                      onClick={() => setDetail((d) => ({ ...d, extracted: { ...d.extracted, units: d.extracted.units.filter((_, x) => x !== i) } }))}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  <Input placeholder={`Unidad ${i + 1} — nombre`} value={u.name} disabled={!editable} data-testid={`planning-unit-name-${i}`}
                    onChange={(e) => setUnit(i, "name", e.target.value)} />
                  <div className="grid sm:grid-cols-2 gap-2">
                    {[["objectives", "Objetivos"], ["topics", "Temas"], ["activities", "Actividades"], ["strategies", "Estrategias"], ["evaluations", "Evaluaciones"]].map(([k, label]) => (
                      <Textarea key={k} rows={2} placeholder={label} value={u[k] || ""} disabled={!editable} data-testid={`planning-unit-${k}-${i}`}
                        onChange={(e) => setUnit(i, k, e.target.value)} />
                    ))}
                  </div>
                </div>
              ))}
              {detail.extracted.units.length === 0 && <p className="text-xs text-slate-400">La IA no identificó unidades. Puedes agregarlas manualmente.</p>}
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
              <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                <h3 className="text-sm font-semibold text-slate-800">Clases diarias ({detail.extracted.lessons.length})</h3>
                {editable && (
                  <Button size="sm" variant="outline" data-testid="planning-add-lesson"
                    onClick={() => setDetail((d) => ({ ...d, extracted: { ...d.extracted, lessons: [...d.extracted.lessons, { ...EMPTY_LESSON }] } }))}>
                    <Plus className="w-4 h-4 mr-1" /> Clase
                  </Button>
                )}
              </div>
              {detail.extracted.lessons.map((l, i) => (
                <div key={i} className="border border-slate-200 rounded-lg p-4 space-y-2 relative" data-testid={`planning-lesson-${i}`}>
                  {editable && (
                    <button className="absolute top-2 right-2 text-slate-300 hover:text-rose-600 transition-colors" data-testid={`planning-lesson-remove-${i}`}
                      onClick={() => setDetail((d) => ({ ...d, extracted: { ...d.extracted, lessons: d.extracted.lessons.filter((_, x) => x !== i) } }))}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  <div className="grid grid-cols-[1fr_130px_90px] gap-2">
                    <Input placeholder="Título de la clase" value={l.title} disabled={!editable} data-testid={`planning-lesson-title-${i}`}
                      onChange={(e) => setLesson(i, "title", e.target.value)} />
                    <Input type="date" value={l.date || ""} disabled={!editable} data-testid={`planning-lesson-date-${i}`}
                      onChange={(e) => setLesson(i, "date", e.target.value)} />
                    <Input type="number" min="1" title="Unidad" value={l.unit} disabled={!editable} data-testid={`planning-lesson-unit-${i}`}
                      onChange={(e) => setLesson(i, "unit", parseInt(e.target.value) || 1)} />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {[["objectives", "Objetivos"], ["activities", "Actividades"], ["evaluation", "Evaluación"]].map(([k, label]) => (
                      <Textarea key={k} rows={2} placeholder={label} value={l[k] || ""} disabled={!editable} data-testid={`planning-lesson-${k}-${i}`}
                        onChange={(e) => setLesson(i, k, e.target.value)} />
                    ))}
                  </div>
                </div>
              ))}
              {detail.extracted.lessons.length === 0 && <p className="text-xs text-slate-400">La IA no identificó clases diarias. Puedes agregarlas manualmente.</p>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="plannings-page">
      <PageHeader title="Planificaciones" subtitle="Sube tu planificación escrita a mano, en PDF o Word — la IA la lee, interpreta y organiza por ti">
        <Button className="bg-blue-800 hover:bg-blue-700" data-testid="import-planning-button" onClick={() => setImportOpen(true)}>
          <Sparkles className="w-4 h-4 mr-1" /> Importar planificación
        </Button>
      </PageHeader>
      <DataTable testid="plannings-table" searchable searchPlaceholder="Buscar planificación…" rows={items} onRowClick={(p) => openDetail(p.id)} columns={[
        { key: "title", label: "Planificación", render: (p) => <span className="font-medium text-slate-900">{p.title}</span> },
        { key: "subject_id", label: "Materia", render: (p) => subjectName(p.subject_id) },
        { key: "course", label: "Curso" },
        { key: "school_year", label: "Año escolar" },
        ...(canApprove ? [{ key: "teacher_name", label: "Docente" }] : []),
        { key: "status", label: "Estado", render: (p) => <StatusPill value={p.status} /> },
        { key: "updated_at", label: "Actualizada", render: (p) => <span className="text-xs text-slate-500">{(p.updated_at || "").slice(0, 10)}</span> },
      ]} />

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Importar planificación con IA</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Archivo (foto de tu planificación a mano, PDF escaneado, Word o texto)</Label>
              <input
                ref={fileRef} type="file" accept="image/*,.pdf,.docx,.txt"
                data-testid="planning-file-input"
                className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-blue-800 file:px-4 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-blue-700 file:cursor-pointer"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Materia</Label>
              <Select value={form.subject_id} onValueChange={(v) => setForm({ ...form, subject_id: v })}>
                <SelectTrigger data-testid="planning-import-subject"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>{subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Curso</Label>
                <Select value={form.course} onValueChange={(v) => setForm({ ...form, course: v })}>
                  <SelectTrigger data-testid="planning-import-course"><SelectValue placeholder="Curso" /></SelectTrigger>
                  <SelectContent>{courses.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Sección</Label>
                <Select value={form.section} onValueChange={(v) => setForm({ ...form, section: v })}>
                  <SelectTrigger data-testid="planning-import-section"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="A">A</SelectItem><SelectItem value="B">B</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Año escolar</Label>
                <Input data-testid="planning-import-year" value={form.school_year} onChange={(e) => setForm({ ...form, school_year: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Interpretar con</Label>
              <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v })}>
                <SelectTrigger data-testid="planning-import-provider"><SelectValue /></SelectTrigger>
                <SelectContent>{PROVIDERS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {analyzing && (
              <div className="flex items-center gap-3 bg-sky-50 border border-sky-200 rounded-xl p-3 text-sm text-sky-800" data-testid="planning-analyzing">
                <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                La IA está leyendo tu documento e identificando secciones… esto puede tardar hasta un minuto.
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" data-testid="planning-import-cancel" onClick={() => setImportOpen(false)} disabled={analyzing}>Cancelar</Button>
            <Button className="bg-blue-800 hover:bg-blue-700" data-testid="planning-import-submit" onClick={doImport} disabled={analyzing}>
              {analyzing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
              Analizar con IA
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
